import { Injectable } from '@angular/core';
import {
	DoubleBooking,
	DoubleBookingEvent,
	DoubleBookingParsed,
	DoubleBookingParsedEvent,
	DoubleBookingParsedInfo,
	EventTimePayload
} from '@models/double-booking.model';
import { ColorService } from '@services/utils/color.service';
import { SettingsProvider } from '@services/settings/settings';
import { filter, map } from 'rxjs/operators';
import { DoubleBookingDataService } from '@services/double-booking/double-boooking.data.service';
import { Observable } from 'rxjs/Observable';

import * as moment from 'moment';
import { BootstrapSettings, ServerDataResponse } from '@models/models';

@Injectable()
export class DoubleBookingService {
	private bootstrapSettings: BootstrapSettings;
	private dateFormat: string;
	private timeFormat: string;
	private DATE_PATTERN = 'YYYY_MM_DD';
	private TIME_PATTERN = 'hh:mm:ss';
	private DATE_PATTERN_SAVE = 'YYYY-MM-DD';
	private TIME_PATTERN_SAVE = 'HH:mm:ss';

	constructor(
		private colorService: ColorService,
		private settingsService: SettingsProvider,
		private doubleBookingDataService: DoubleBookingDataService
	) {
		this.getCalendarPreferences();
	}

	getOverlappingEvents(
		data: EventTimePayload,
		type?: 'JOB' | 'EVENT',
		id?: number
	): Observable<DoubleBookingParsed> {
		return this.doubleBookingDataService
			.getOverlappingEvents(data, type, id)
			.pipe(
				map(
					(res: ServerDataResponse<DoubleBooking>): DoubleBooking => res.data
				),
				map((db: DoubleBooking): DoubleBookingParsed | null =>
					this.mapDbResponse(db)
				)
			);
	}

	gatherDateTimeData({
		startDate,
		finishDate,
		startTime,
		finishTime,
		allday
	}): EventTimePayload {
		const timePattern =
			this.bootstrapSettings.timeFormat === 'T_12'
				? 'h:mm A'
				: this.TIME_PATTERN_SAVE;

		const parsedData: EventTimePayload = {
			startDate: moment(startDate).format(this.DATE_PATTERN_SAVE),
			endDate: moment(finishDate).format(this.DATE_PATTERN_SAVE)
		};

		if (!allday) {
			parsedData.startTime = moment(startTime, timePattern).format(
				this.TIME_PATTERN_SAVE
			);
			parsedData.endTime = moment(finishTime, timePattern).format(
				this.TIME_PATTERN_SAVE
			);
		}
		return parsedData;
	}

	sortBrands(
		companiesList: Array<string>,
		data: DoubleBookingParsedInfo | DoubleBookingParsed
	): Array<string> {
		if (companiesList.length > 1) {
			return companiesList.sort(
				(companyA: string, companyB: string): number => {
					if (data.events[companyA][0].company.currentBrand) {
						return -1;
					}

					if (data.events[companyB][0].company.currentBrand) {
						return 1;
					}

					return companyA.localeCompare(companyB);
				}
			);
		}

		return companiesList;
	}

	sortDoubleBookingEvents(
		data: DoubleBookingParsedInfo | DoubleBookingParsed
	): DoubleBookingParsedInfo | DoubleBookingParsed {
		const companies = Object.keys(data.events);
		const sortedData: DoubleBookingParsedInfo = JSON.parse(
			JSON.stringify(data)
		);

		companies.forEach((company: string): void => {
			if (sortedData.events[company].length > 1) {
				sortedData.events[company] = sortedData.events[company].sort(
					this.sortEvents.bind(this)
				);
			}
		});

		return sortedData;
	}

	parseEventsToShow(
		events: Array<DoubleBookingEvent>
	): DoubleBookingParsedInfo {
		const result: DoubleBookingParsedInfo = {
			titleText: null,
			events: {}
		};

		const [primaryEvent] = events.filter(
			(ev: DoubleBookingEvent): boolean => ev.primaryEvent
		);
		const datesSet: Set<string> = this.createNewDateSet(events, primaryEvent);
		const allDatesSet: Set<string> = this.createNewDateSet(events);

		const sortedDateRange = Array.from(datesSet).sort(
			(a: string, b: string): number => moment(a).unix() - moment(b).unix()
		);

		let shouldShowDate;
		Array.from(allDatesSet).length === 1
			? (shouldShowDate = false)
			: (shouldShowDate = true);

		result.titleText = this.generateDateRangeText(sortedDateRange);

		const newEvents = events.map(
			(event: DoubleBookingEvent): DoubleBookingParsedEvent =>
				this.convertEventDataToShow(event, shouldShowDate)
		);

		this.groupEventsByBrand(newEvents, result);

		return result;
	}

	private generateDateRangeText(sortedDateRange: Array<string>): string {
		const start = moment(sortedDateRange[0], this.DATE_PATTERN).format(
			this.dateFormat
		);
		const end = moment(
			sortedDateRange[sortedDateRange.length - 1],
			this.DATE_PATTERN
		).format(this.dateFormat);

		if (sortedDateRange.length > 1) {
			return `Overlapping Events from ${start} - ${end}`;
		} else if (sortedDateRange.length === 1) {
			return `Overlapping Events on ${start}`;
		}
		return 'Overlapping Events';
	}

	private convertEventDataToShow(
		event: DoubleBookingEvent,
		shouldShowDate: boolean
	): DoubleBookingParsedEvent {
		const { startDate, finishDate, startTime, finishTime } = event.timePeriod;
		let parsedStartDate = '';
		let parsedFinishDate = '';
		let parsedTime = '';
		let daysAmount = '';

		if (startDate) {
			parsedStartDate += moment(startDate, this.DATE_PATTERN).format(
				this.dateFormat
			);
		}

		if (!startTime && !finishTime) {
			parsedTime += ' All day ';
		}

		if (startTime && finishTime) {
			parsedTime += ` 
				${moment(startTime, this.TIME_PATTERN).format(this.timeFormat)} - 
				${moment(finishTime, this.TIME_PATTERN).format(this.timeFormat)}
			`;
		}

		if (startDate !== finishDate) {
			const daysDiff = moment(finishDate, this.DATE_PATTERN).diff(
				moment(startDate, this.DATE_PATTERN),
				'days'
			);
			daysAmount += `(+${daysDiff})`;

			parsedFinishDate += moment(finishDate, this.DATE_PATTERN).format(
				this.dateFormat
			);
		}

		return {
			...event,
			jobType: {
				...event.jobType,
				shadedColor: this.colorService.shadeColor(event.jobType.hexColor, -20)
			},
			parsedStartDate,
			parsedFinishDate,
			parsedTime,
			daysAmount,
			shouldShowDate
		};
	}

	private groupEventsByBrand(
		parsedEvents: Array<DoubleBookingEvent>,
		result: DoubleBookingParsedInfo
	): void {
		parsedEvents.forEach((event: DoubleBookingParsedEvent): void => {
			const { companyName } = event.company;

			if (!result.events[companyName]) {
				result.events[companyName] = [];
			}

			result.events[companyName].push(event);
		});
	}

	private sortEvents(
		prevEvent: DoubleBookingParsedEvent,
		nextEvent: DoubleBookingParsedEvent
	): number {
		const prevTime = prevEvent.timePeriod;
		const nextTime = nextEvent.timePeriod;

		const isSameDays = prevTime.startDate === nextTime.startDate;
		const isAllDayAndTime = !prevTime.startTime && nextTime.startTime;
		const isTimeAndAllDay = prevTime.startTime && !nextTime.startTime;
		const isSameTime = prevTime.startTime === nextTime.startTime;
		const isPriorityEqual = prevEvent.priority === nextEvent.priority;

		if (!isSameDays) {
			return (
				moment(prevTime.startDate, this.DATE_PATTERN).unix() -
				moment(nextTime.startDate, this.DATE_PATTERN).unix()
			);
		}

		if (isSameDays) {
			if (isAllDayAndTime) {
				return -1;
			}

			if (isTimeAndAllDay) {
				return 1;
			}

			if (!isSameTime) {
				return moment(prevTime.startTime, this.TIME_PATTERN).isBefore(
					moment(nextTime.startTime, this.TIME_PATTERN)
				)
					? -1
					: 1;
			}

			if (isSameTime) {
				if (!isPriorityEqual) {
					return prevEvent.priority < nextEvent.priority ? -1 : 1;
				}

				if (isPriorityEqual) {
					return prevEvent.eventName.localeCompare(nextEvent.eventName);
				}
			}
		}
	}

	private getCalendarPreferences(): void {
		this.settingsService.timeFormatSettings
			.pipe(
				filter(
					(settings: BootstrapSettings): boolean =>
						!!settings.dateFormat && !!settings.timeFormat
				)
			)
			.subscribe((data: BootstrapSettings): void => {
				this.bootstrapSettings = data;
				this.dateFormat = data.dateFormat
					.replace('MM', 'MMM')
					.replace(/_/g, ' ');
				this.timeFormat = data.timeFormat === 'T_12' ? 'h:mma' : 'H:mm';
			});
	}

	private mapDbResponse(db: DoubleBooking): DoubleBookingParsed | null {
		if (!db) {
			return db as null;
		}
		const { events } = this.parseEventsToShow(db.events);
		return { ...db, events } as DoubleBookingParsed;
	}

	private createNewDateSet(
		events: Array<DoubleBookingEvent>,
		primaryEvent?: DoubleBookingEvent
	): Set<string> {
		const datesSet: Set<string> = new Set<string>();

		if (primaryEvent) {
			const { startDate, finishDate } = primaryEvent.timePeriod;
			datesSet.add(startDate);
			events.forEach((event: DoubleBookingEvent): void => {
				if (startDate && finishDate && !event.primaryEvent) {
					if (startDate === event.timePeriod.startDate) {
						datesSet.add(event.timePeriod.startDate);
					}
					if (moment(startDate).isBefore(event.timePeriod.startDate)) {
						datesSet.add(event.timePeriod.startDate);
					}

					if (finishDate === event.timePeriod.finishDate) {
						datesSet.add(event.timePeriod.finishDate);
					}
					if (moment(event.timePeriod.finishDate).isBefore(finishDate)) {
						datesSet.add(event.timePeriod.finishDate);
					}
				}
			});
		} else {
			events.forEach((event: DoubleBookingEvent): void => {
				datesSet.add(event.timePeriod.startDate);
				datesSet.add(event.timePeriod.finishDate);
			});
		}

		return datesSet;
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./shared/services/double-booking/double-booking.service.ts